/* eslint-disable */

import React from "react";
import { HiX } from "react-icons/hi";
import Links from "./components/Links";
import { useAppLanguage } from "hooks/useAppLanguage";
import appLogo from "assets/img/layout/easyfollow-logo.png";

import routes from "routes";

export type SidebarPortal = "admin" | "instructor";

const portalBranding: Record<
  SidebarPortal,
  {
    badgeAr: string;
    badgeEn: string;
    titleAr: string;
    titleEn: string;
    accentClass: string;
    badgeClass: string;
    activeClass: string;
  }
> = {
  admin: {
    badgeAr: "إدارة النظام",
    badgeEn: "Super Admin",
    titleAr: "مركز التحكم",
    titleEn: "Control Center",
    accentClass: "from-emerald-500 via-teal-500 to-cyan-500",
    badgeClass: "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300",
    activeClass: "bg-emerald-500/10 dark:bg-emerald-400/10",
  },
  instructor: {
    badgeAr: "بوابة المحاضر",
    badgeEn: "Instructor Portal",
    titleAr: "مساحة التدريس",
    titleEn: "Teaching Hub",
    accentClass: "from-orange-500 via-amber-500 to-yellow-400",
    badgeClass: "bg-orange-500/10 text-orange-700 dark:bg-orange-400/15 dark:text-orange-300",
    activeClass: "bg-orange-500/10 dark:bg-orange-400/10",
  },
};

const Sidebar = (props: {
  open: boolean;
  onClose: React.MouseEventHandler<HTMLSpanElement>;
  sidebarLayout?: "/admin" | "/instructor";
}) => {
  const { open, onClose, sidebarLayout = "/admin" } = props;
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const portal: SidebarPortal = sidebarLayout === "/instructor" ? "instructor" : "admin";
  const brand = portalBranding[portal];

  // Lock body scroll when the drawer is open on mobile/tablet (below xl = 1200px).
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(max-width: 1199px)");
    const applyLock = () => {
      const shouldLock = open && mediaQuery.matches;
      document.body.style.overflow = shouldLock ? "hidden" : "";
    };
    applyLock();
    const handleChange = () => applyLock();
    mediaQuery.addEventListener?.("change", handleChange);
    return () => {
      mediaQuery.removeEventListener?.("change", handleChange);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      {/* Backdrop overlay — visible on mobile/tablet only, closes drawer on tap. */}
      <div
        onClick={onClose as any}
        aria-hidden="true"
        className={`apple-backdrop fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-[2px] transition-opacity duration-200 dark:bg-black/60 xl:hidden ${
          open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
      />

      <aside
        className={`apple-sidebar duration-300 fixed top-0 !z-50 flex h-screen w-[86vw] max-w-[304px] flex-col overflow-hidden border-gray-200 bg-white shadow-2xl shadow-gray-200/40 transition-transform dark:border-white/[0.06] dark:bg-sidebar dark:text-slate-50 dark:shadow-[4px_0_32px_-8px_rgba(0,0,0,0.55)] sm:w-[288px] md:!z-50 lg:!z-50 xl:w-[288px] xl:!z-0 ${
          isArabic ? "right-0 border-l" : "left-0 border-r"
        } ${
          open ? "translate-x-0" : isArabic ? "translate-x-full" : "-translate-x-full"
        }`}
      >
        <span
          className={`absolute top-4 block cursor-pointer rounded-lg p-1 text-gray-600 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-white/[0.06] xl:hidden ${
            isArabic ? "left-4" : "right-4"
          }`}
          onClick={onClose}
        >
          <HiX className="h-5 w-5" />
        </span>

        <div className="apple-sidebar-brand mx-3 mt-4 shrink-0 overflow-hidden rounded-[24px] border border-gray-200/80 bg-gray-50/80 dark:border-white/[0.08] dark:bg-surface-elevated/80">
          <div className={`h-1.5 bg-gradient-to-r ${brand.accentClass}`} />
          <div className="flex items-center gap-3 p-3.5">
            <img src={appLogo} alt="Easyfollow Platform" className="apple-sidebar-logo h-12 w-12 rounded-2xl object-contain shadow-sm" />
            <div className="min-w-0">
              <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${brand.badgeClass}`}>
                {isArabic ? brand.badgeAr : brand.badgeEn}
              </span>
              <p className="mt-1 truncate text-sm font-extrabold text-navy-700 dark:text-slate-100">
                {isArabic ? brand.titleAr : brand.titleEn}
              </p>
            </div>
          </div>
        </div>

        <div className="apple-sidebar-divider mx-5 mb-3 mt-4 h-px shrink-0 bg-gray-200 dark:bg-white/[0.08]" />

        <ul className="sidebar-scroll min-h-0 flex-1 overflow-y-auto px-2 pb-10 pt-1">
          <Links routes={routes} sidebarLayout={sidebarLayout} portal={portal} />
        </ul>
      </aside>
    </>
  );
};

export default Sidebar;
